import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined, DeleteOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Select, Space, Table, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import PageHeader from '@shared/PageHeader';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import InventorySelectorModal from '@components/shared/selectors/InventorySelectorModal';
import useSmartBack from '@hooks/shared/useSmartBack';

import { useMerchant } from '@contexts/admin/MerchantContext';
import { useAdminProductService } from '@hooks/admin/products/useAdminProductService';
import { inventoryService as adminInventoryService } from '@services/admin/inventoryService';
import { inventoryTypeService as adminInventoryTypeService } from '@services/admin/inventoryTypeService';
import { inventoryTransformationService, type InventoryTransformationCreateInput } from '@services/admin/inventoryTransformationService';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

import AdminTransformationItemModal from './components/AdminTransformationItemModal';

interface Warehouse {
  id: string;
  name: string;
}

interface InventoryTypeOption {
  id: string;
  name: string;
  full_name?: string;
  code?: string;
}

interface DraftItem {
  inventory_id: string;
  quantity: number;
  target_inventory_type_id: string;
  reason_code?: string;
  note?: string;
  __product_sku?: string;
  __product_name?: string;
  __batch?: string | null;
  __source_inventory_type_name?: string;
  __target_inventory_type_name?: string;
  // Local-only id used as React key.
  __key: string;
}

export default function AdminInventoryTransformationNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const productService = useAdminProductService();
  const { warehouses, defaultWarehouseId, currentMerchant } = useMerchant() as {
    warehouses: Warehouse[];
    defaultWarehouseId: string | null;
    currentMerchant: { id?: string } | null;
  };

  const [form] = Form.useForm<{ name?: string; warehouse_id?: string; note?: string }>();
  const [warehouseId, setWarehouseId] = useState<string | undefined>(defaultWarehouseId ?? undefined);
  const [items, setItems] = useState<DraftItem[]>([]);
  const [inventoryTypes, setInventoryTypes] = useState<InventoryTypeOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const [selectorVisible, setSelectorVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<unknown>(null);

  // Load merchant's inventory types once — small list, fetched up-front so the
  // item modal can render synchronously when a row is picked.
  useEffect(() => {
    let cancelled = false;
    setLoadingTypes(true);
    adminInventoryTypeService.fetchList()
      .then(res => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setInventoryTypes(res.data);
        }
      })
      .catch(err => console.error('Failed to load inventory types', err))
      .finally(() => { if (!cancelled) setLoadingTypes(false); });
    return () => { cancelled = true; };
  }, []);

  // Sync form field when warehouseId state changes (so validation stays happy).
  useEffect(() => {
    if (warehouseId) form.setFieldValue('warehouse_id', warehouseId);
  }, [warehouseId, form]);

  // Initial default warehouse.
  useEffect(() => {
    if (!warehouseId && defaultWarehouseId) {
      setWarehouseId(defaultWarehouseId);
    }
  }, [defaultWarehouseId, warehouseId]);

  const handleSuggestName = async () => {
    setSuggesting(true);
    try {
      const res = await inventoryTransformationService.suggestName();
      if (res.success) {
        form.setFieldValue('name', res.data.name);
      } else {
        handleApiError(res, { t });
      }
    } catch (err) {
      handleNetworkError(err, { t });
    } finally {
      setSuggesting(false);
    }
  };

  const handleWarehouseChange = (id: string) => {
    if (items.length > 0) {
      // Switching warehouse invalidates already-picked inventories.
      // Clear the draft to keep the merchant's intent self-consistent.
      setItems([]);
      message.info(t('shared.inventory_transformation.warehouse_changed_items_cleared',
        '已切換倉庫，先前選擇的明細已清空'));
    }
    setWarehouseId(id);
  };

  const openSelector = () => {
    if (!warehouseId) {
      message.warning(t('shared.inventory_transformation.pick_warehouse_first', '請先選擇倉庫'));
      return;
    }
    setSelectorVisible(true);
  };

  const handleInventoryPicked = (inventory: unknown) => {
    setSelectedInventory(inventory);
    setSelectorVisible(false);
    setItemModalVisible(true);
  };

  const handleItemConfirm = (item: Omit<DraftItem, '__key'>) => {
    setItems(prev => [...prev, { ...item, __key: `${Date.now()}-${prev.length}` }]);
    setItemModalVisible(false);
    setSelectedInventory(null);
  };

  const handleRemove = (key: string) => {
    setItems(prev => prev.filter(i => i.__key !== key));
  };

  const lastReasonCode = items.length > 0 ? items[items.length - 1].reason_code : undefined;

  const itemColumns = useMemo(() => [
    {
      title: t('models.product.name'),
      key: 'product',
      render: (_: unknown, r: DraftItem) => (
        <div>
          <div>{r.__product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.__product_name}</div>
        </div>
      ),
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: '__batch',
      render: (v?: string | null) => v ?? '-',
    },
    {
      title: t('models.inventory_transformation.inventory_type_change'),
      key: 'type_change',
      render: (_: unknown, r: DraftItem) => (
        <span>
          <Tag>{r.__source_inventory_type_name ?? '-'}</Tag>
          <ArrowRightOutlined style={{ margin: '0 4px' }} />
          <Tag color="blue">{r.__target_inventory_type_name ?? '-'}</Tag>
        </span>
      ),
    },
    {
      title: t('models.inventory_transformation_item.quantity'),
      dataIndex: 'quantity',
      align: 'right' as const,
    },
    {
      title: t('models.inventory_transformation_item.reason_code'),
      dataIndex: 'reason_code',
      render: (v?: string) => v
        ? t(`enums.stock_adjustment.reason_code.${v}`, v)
        : '-',
    },
    {
      title: t('models.inventory_transformation_item.note'),
      dataIndex: 'note',
      render: (v?: string) => v ?? '-',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (_: unknown, r: DraftItem) => (
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemove(r.__key)} />
      ),
    },
  ], [t]);

  const handleSubmit = async () => {
    let values: { name?: string; warehouse_id?: string; note?: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (!warehouseId) {
      message.error(t('shared.inventory_transformation.pick_warehouse_first', '請先選擇倉庫'));
      return;
    }
    if (items.length === 0) {
      message.error(t('shared.inventory_transformation.error.no_items', '請至少新增一筆明細'));
      return;
    }

    // form 內 reason_code 是 optional string，但 service 的 CreateInput 要求必填。
    // 沒填等於前端校驗洩漏，stop 並提示，不送出去讓 BE 退。
    const missingReason = items.find(i => !i.reason_code);
    if (missingReason) {
      message.error(t('shared.inventory_transformation.error.missing_reason_code', '請為每一筆明細選擇原因'));
      return;
    }

    // form state 帶 `undefined`、JsonObject body 不接受 undefined（JSON spec 不允許）。
    // 在 payload construction 顯式 `?? null` 把 optional field normalize 成「明確送 null」。
    const payload: InventoryTransformationCreateInput = {
      warehouse_id: warehouseId,
      name: values.name ?? null,
      note: values.note ?? null,
      items: items.map(i => ({
        inventory_id: i.inventory_id,
        quantity: i.quantity,
        target_inventory_type_id: i.target_inventory_type_id,
        reason_code: i.reason_code as string,
        note: i.note ?? null,
      })),
    };

    setSubmitting(true);
    try {
      const response = await inventoryTransformationService.create(payload);
      if (response.success) {
        message.success(t('common.message.create_success'));
        navigate(`/inventory_transformations/${response.data.id}`, { replace: true });
      } else {
        handleApiError(response, { t });
      }
    } catch (err) {
      handleNetworkError(err, { t });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        basePath="/admin"
        items={['stocks', 'stocks.inventory_transformations',
          { label: t('shared.inventory_transformation.new_title', '新增庫存轉換') }]}
      />

      <Card title={t('shared.inventory_transformation.basic_info')} bordered={false} style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Form.Item
            label={t('models.inventory_transformation.warehouse')}
            name="warehouse_id"
            rules={[{ required: true, message: t('common.form.required') }]}
          >
            <Select
              value={warehouseId}
              onChange={handleWarehouseChange}
              options={warehouses.map(w => ({ value: w.id, label: w.name }))}
              placeholder={t('shared.inventory_transformation.warehouse_placeholder', '選擇倉庫')}
              style={{ maxWidth: 320 }}
            />
          </Form.Item>

          <Form.Item label={t('models.inventory_transformation.name')}>
            <Space.Compact style={{ width: 320 }}>
              <Form.Item name="name" noStyle>
                <Input placeholder={t('shared.inventory_transformation.name_placeholder', '自動產生或自行輸入')} />
              </Form.Item>
              <Button onClick={handleSuggestName} loading={suggesting} disabled={submitting}>
                {t('shared.inventory_transformation.suggest_name', '自動產生')}
              </Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item label={t('models.inventory_transformation.note')} name="note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={t('shared.inventory_transformation.items')}
        bordered={false}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openSelector} disabled={!warehouseId || loadingTypes}>
            {t('common.button.add')}
          </Button>
        }
      >
        <Table
          columns={itemColumns}
          dataSource={items}
          rowKey="__key"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('shared.inventory_transformation.no_items', '尚未新增明細') }}
        />
      </Card>

      <FloatingActionBarSpacer />
      <FloatingActionBar
        role="admin"
        onCancel={goBack}
        onSubmit={handleSubmit}
        loading={submitting}
        submitText={t('common.button.submit')}
      />

      <InventorySelectorModal
        visible={selectorVisible}
        onCancel={() => setSelectorVisible(false)}
        onSelect={handleInventoryPicked}
        merchantId={currentMerchant?.id}
        warehouseId={warehouseId}
        inventoryService={adminInventoryService}
        productService={productService}
      />

      <AdminTransformationItemModal
        visible={itemModalVisible}
        inventory={selectedInventory as never}
        existingItems={items}
        inventoryTypes={inventoryTypes}
        defaultReasonCode={lastReasonCode}
        onCancel={() => setItemModalVisible(false)}
        onConfirm={handleItemConfirm}
      />
    </div>
  );
}
